/*
 * Settings' pages, as data: the rail on Settings, the command palette's "Go to" and the breadcrumbs name the same
 * ones, by the same rules. Each page is its own address (/settings/:id); the rail groups them under small headings:
 * your own account, the organization, its sites, its brand, and Hosterr's own (platform admins).
 *
 * Who sees what: everyone has their account; a billing member has Billing and General (read only); a client
 * has the org's General and Team; the rest by role (Alerts for admins, Branding for those who brand, Hosterr's for its
 * admins). A page opened by its address stays in the rail even when it isn't one of these (the API decides what's in it).
 */
import { useBranding } from "../api/agency";
import type { MeOrg } from "../api/types";
import { canBrand, isAdmin, seesBilling } from "../lib/roles";
import { usePlatformAdmin } from "./accounts";
import { useOrg, useOrgPath } from "./org";

export const SETTINGS_GROUPS = ["Your account", "Organization", "Sites", "Brand", "Hosterr"] as const;
export type SettingsGroup = (typeof SETTINGS_GROUPS)[number];

/** Every Settings page's address after /settings/, in rail order. */
export const SETTINGS_PAGES = [
  "profile",
  "sign-in",
  "ssh-keys",
  "api-tokens",
  "notifications",
  "organization",
  "team",
  "billing",
  "tags",
  "alerts",
  "security",
  "branding",
  "branding/email",
  "branding/addresses",
  "domain-prices",
  "plans",
  "backup-policies",
  "agent-updates",
  "legal",
] as const;
export type SettingsPageId = (typeof SETTINGS_PAGES)[number];

type PageDef = { label: string; group: SettingsGroup; words: string };
const PAGE: Record<SettingsPageId, PageDef> = {
  profile: { label: "Profile", group: "Your account", words: "account profile name email appearance theme dark light" },
  "sign-in": { label: "Sign-in and security", group: "Your account", words: "password two-factor 2fa authenticator recovery codes passkeys sessions sign out" },
  "ssh-keys": { label: "SSH keys", group: "Your account", words: "ssh keys sftp git public key" },
  "api-tokens": { label: "API tokens", group: "Your account", words: "api tokens access" },
  notifications: { label: "Notifications", group: "Your account", words: "notifications email alerts bell recently sent" },
  organization: { label: "General", group: "Organization", words: "organization name time zone domain contact registrant" },
  team: { label: "Team", group: "Organization", words: "team members people invite invites groups roles two-factor" },
  billing: { label: "Billing", group: "Organization", words: "billing invoices card payment plan" },
  tags: { label: "Site tags", group: "Organization", words: "site tags colors" },
  alerts: { label: "Alerts", group: "Organization", words: "alerts slack discord webhook email sent" },
  security: { label: "Site security", group: "Sites", words: "site security profiles firewall saved address lists" },
  branding: { label: "Logo and colors", group: "Brand", words: "branding brand look logo colors color name white label" },
  "branding/email": { label: "Email and support", group: "Brand", words: "branding sender name support email support page terms" },
  "branding/addresses": { label: "Custom addresses", group: "Brand", words: "branding custom addresses own domain panel webmail address" },
  "domain-prices": { label: "Domain prices", group: "Hosterr", words: "domain prices tld opensrs" },
  plans: { label: "Plans", group: "Hosterr", words: "plans pricing" },
  "backup-policies": { label: "Backup policies", group: "Hosterr", words: "backup policies retention" },
  "agent-updates": { label: "Agent updates", group: "Hosterr", words: "agent updates rollout rollback pause servers" },
  legal: { label: "Legal", group: "Hosterr", words: "legal terms privacy acceptable use dpa" },
};

export type SettingsPage = PageDef & { id: SettingsPageId; to: string };

/** A page's name ("Sign-in and security"), for the breadcrumbs. */
export const settingsLabel = (id: string): string | null => (id in PAGE ? PAGE[id as SettingsPageId].label : null);

/**
 * The page an address under /settings/ belongs to: itself, or for a page inside one, its parent (a person and a
 * group are Team's, a security profile is Site security's). Null when it isn't a Settings page.
 */
export function settingsPageOf(rest: string): SettingsPageId | null {
  const [a = "", b] = rest.split("/").filter(Boolean);
  if (a === "people" || a === "groups") return "team";
  if (a === "branding" && b) return (SETTINGS_PAGES as readonly string[]).includes(`branding/${b}`) ? (`branding/${b}` as SettingsPageId) : null;
  return (SETTINGS_PAGES as readonly string[]).includes(a) ? (a as SettingsPageId) : null;
}

/**
 * Whether this person gets a page here. `platformAdmin`: they're one of Hosterr's admins (API tokens are theirs, in
 * any org). `ownBrand`: the brand's pages past Logo and colors (an agency on its own brand; Hosterr's own); null while not known.
 */
export function showsSettingsPage(id: SettingsPageId, org: MeOrg, platformAdmin: boolean, ownBrand: boolean | null): boolean {
  const role = org.role;
  const billingOnly = role === "billing";
  const client = role === "client";
  const hosterr = org.kind === "platform" && isAdmin(role);
  switch (id) {
    case "ssh-keys":
      return !billingOnly && !client;
    case "api-tokens":
      return platformAdmin;
    case "organization":
      return true; // billing members too, read only
    case "team":
      return !billingOnly;
    case "billing":
      return seesBilling(org);
    case "tags":
    case "security":
      return !billingOnly && !client;
    case "alerts":
      return isAdmin(role);
    case "branding":
      return canBrand(org);
    case "branding/email":
      return canBrand(org) && ownBrand === true;
    case "branding/addresses":
      return canBrand(org) && org.kind === "agency" && ownBrand === true;
    case "domain-prices":
    case "plans":
    case "backup-policies":
    case "agent-updates":
    case "legal":
      return hosterr;
    default:
      return true;
  }
}

/** This person's Settings pages here, in rail order; `current` stays listed when it's opened by its address. */
export function useSettingsPages(current?: SettingsPageId | null): SettingsPage[] {
  const path = useOrgPath();
  const org = useOrg();
  const platformAdmin = usePlatformAdmin();
  const brands = canBrand(org);
  const branding = useBranding(brands);
  // Hosterr's own brand is always its own; an agency's is once it chose its own (an older API: its own).
  const ownBrand = !brands ? false : org.kind !== "agency" ? true : branding.data ? branding.data.ownBrand !== false : null;
  return SETTINGS_PAGES.filter((id) => id === current || showsSettingsPage(id, org, platformAdmin, ownBrand)).map((id) => ({ id, to: path(`settings/${id}`), ...PAGE[id] }));
}
